import { useEffect, useState } from 'react';
import { Button, Modal } from '@react-apps/design-system';
import { Textarea } from '@/shared/components/textarea';
import { describeFileError } from '../format';
import { useSetLegalHold } from '../hooks/use-stored-files';

const MAX_REASON = 500;

/**
 * Ligar ou desligar o legal hold. Desligar exige motivo, porque é o que libera
 * o force-delete (Decisão 2 do épico files-admin); ligar aceita motivo sem
 * exigir.
 */
export function LegalHoldModal({
  fileId,
  legalHold,
  open,
  onClose,
}: {
  fileId: string;
  /** O estado atual: o modal propõe o contrário. */
  legalHold: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useSetLegalHold(fileId);

  useEffect(() => {
    if (open) {
      setReason('');
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const turningOff = legalHold;
  const trimmed = reason.trim();
  const tooLong = trimmed.length > MAX_REASON;
  const blocked =
    (turningOff && trimmed === '') || tooLong || mutation.isPending;
  const action = turningOff ? 'Desligar legal hold' : 'Ligar legal hold';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={action}
      description={
        turningOff
          ? 'Sem o legal hold, o arquivo pode ser apagado.'
          : 'Com o legal hold ligado, ninguém apaga o arquivo até ele ser desligado.'
      }
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button
            onClick={() =>
              mutation.mutate(
                { legalHold: !legalHold, reason: trimmed || undefined },
                { onSuccess: onClose },
              )
            }
            disabled={blocked}
          >
            {mutation.isPending ? 'Salvando…' : action}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {mutation.isError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {describeFileError(
              mutation.error,
              'O legal hold não foi alterado. Tente de novo.',
            )}
          </p>
        )}
        <Textarea
          label={turningOff ? 'Motivo (obrigatório)' : 'Motivo (opcional)'}
          value={reason}
          onChange={setReason}
          rows={3}
          helperText={
            tooLong
              ? `O motivo passou de ${MAX_REASON} caracteres.`
              : 'Fica na auditoria, junto com quem alterou. Não escreva nome, documento ou e-mail.'
          }
        />
      </div>
    </Modal>
  );
}
